// Scherm 3 — de zoekoverlay. Vorm uit de dummy (V1 r. 797-830): donkere waas met
// blur, kaart van 660px met radius 16, zoekregel met esc-knop, resultaatrijen
// met hover #f9fafb.
//
// Wat anders is (W8 scherm 3, oordeel "anders — over alle soorten"): de dummy
// zocht alleen in skills. Deze overlay zoekt in trends, skills én bronnen —
// drie aanroepen, één lijst, elke rij met zijn soort ervoor. Er is bewust geen
// gecombineerd zoek-endpoint: drie kleine vragen zijn goedkoper dan een nieuwe
// zoeklaag, en elke lijst houdt zijn eigen publicatiefilter.
//
// Testfeedback 03-11 (rij 3, owner koos variant A): regelingen zoeken mee, een
// rij soortknoppen (Alles · Trends · Skills · Bronnen · Regelingen) beperkt de
// vraag tot één soort, en elke groep verschijnt zodra hij binnen is — de
// bronnen kunnen traag zijn, dan wacht de rest daar niet meer op.

import { useEffect, useRef, useState } from "react";

import type { Funding, Skill, Source, Trend } from "../contracts";
import { api, type TrendlaagTrend } from "../lib/api";
import { haalFundingLijst } from "../lib/funding";
import { useMeta } from "../lib/meta";
import { useActieveSector } from "../lib/sectorkeuze";
import { toonPropositie } from "../nieuw/propositie";
import { zoekVoorbeelden } from "./zoekVoorbeelden";
import { trendsVoorKaart } from "../nieuw/trendlaag/kaart";
import { zoekTreft } from "../nieuw/trendlaag/lijst";
import { trendlaagAan } from "../nieuw/trendlaag/schakelaar";
// Kleuren via de chattokens: in de nieuwe weergave volgt het venster licht en
// donker, in de oude dezelfde waarden als voorheen (testfeedback 03-11, rij 6).
import { color, radius, size, weight } from "./chatTokens";
import { WIE_WOORD } from "./Bewijs";
import { LaagTrend } from "./TrendlaagOud";

/** Trendlaag aan (dekking eindstand 25-10): zoeken in de trends van de trendlaag,
 *  op titel, subtekst, onderwerp en de titels en werkzinnen van de ontwikkelingen.
 *  Client-side op de lijst die de trendkaart ook gebruikt; geen uitlichtscore. */
export function zoekLaagTrends(trends: readonly TrendlaagTrend[], q: string, max = 5): { trend: TrendlaagTrend; meta: string }[] {
  const z = q.trim().toLowerCase();
  const uit: { trend: TrendlaagTrend; meta: string }[] = [];
  for (const t of trends) {
    const viaOntw = t.ontwikkelingen.find((o) => o.titel.toLowerCase().includes(z) || (o.werkzin ?? "").toLowerCase().includes(z));
    if (!(zoekTreft(t, z) || t.subtekst.toLowerCase().includes(z) || viaOntw)) continue;
    const ontwTitel = !t.titel.toLowerCase().includes(z) && viaOntw ? viaOntw.titel : null;
    const meta = [t.onderwerp, `${t.bronnen ?? 0} bronnen`, ontwTitel ? `ontwikkeling: ${ontwTitel}` : null].filter(Boolean).join(" · ");
    uit.push({ trend: t, meta });
    if (uit.length >= max) break;
  }
  return uit;
}

type Hit =
  | { kind: "trend"; id: string; title: string; meta: string; score?: number; trend: Trend }
  | { kind: "skill"; id: string; title: string; meta: string; score?: number; skill: Skill }
  | { kind: "bron"; id: string; title: string; meta: string; score?: number; source: Source }
  | { kind: "laagtrend"; id: string; title: string; meta: string; score?: undefined }
  | { kind: "regeling"; id: string; title: string; meta: string; score?: undefined; funding: Funding };

const KIND_LABEL: Record<Hit["kind"], string> = {
  trend: "trend",
  skill: "skill",
  bron: "bron",
  laagtrend: "trend",
  regeling: "regeling",
};

/** De vier groepen in vaste volgorde; "alles" is geen groep maar de knop die ze alle vier toont. */
export type ZoekSoort = "trend" | "skill" | "bron" | "regeling";
const GROEPEN: readonly ZoekSoort[] = ["trend", "skill", "bron", "regeling"];
const GROEP_TITEL: Record<ZoekSoort, string> = { trend: "Trends", skill: "Skills", bron: "Bronnen", regeling: "Regelingen" };
const PLAATSHOUDER: Record<ZoekSoort | "alles", string> = {
  alles: "Zoek in trends, skills, bronnen en regelingen…",
  trend: "Zoek in de trends…",
  skill: "Zoek in de skills…",
  bron: "Zoek in de bronnen…",
  regeling: "Zoek in de regelingen…",
};
const STATUS_WOORD: Record<Funding["status"], string> = {
  open: "open",
  aangekondigd: "aangekondigd",
  gesloten: "gesloten",
  onbekend: "status onbekend",
};
/** Per groep: nog bezig, mislukt, of de treffers. */
type GroepStand = "bezig" | "fout" | Hit[];

export function SearchOverlay({
  onClose,
  onSelectTrend,
  onSelectSkill,
  onSelectSource,
  onSelectFunding,
  regelSectoren,
}: {
  onClose: () => void;
  onSelectTrend?: (t: Trend) => void;
  onSelectSkill?: (s: Skill) => void;
  onSelectSource?: (s: Source) => void;
  onSelectFunding?: (f: Funding) => void;
  /** Regelingen staan per bedrijfstak in het register, zoals op de Funding-tab
   *  (registerSectoren van wie er kijkt); leeg = het hele register. */
  regelSectoren?: readonly string[];
}) {
  const [query, setQuery] = useState("");
  const [soort, setSoort] = useState<ZoekSoort | "alles">("alles");
  const [groepen, setGroepen] = useState<Partial<Record<ZoekSoort, GroepStand>>>({});
  // Trendlaag aan: de gekozen trend opent in de overlay zelf (de trendlaag-kaart).
  const [laagOpen, setLaagOpen] = useState<{ id: string; titel: string } | null>(null);
  const laagAan = trendlaagAan();
  const inputRef = useRef<HTMLInputElement>(null);
  // ADR 2026-10-23-ict-los-platform: met meer dan één zichtbare propositie
  // zoekt de balk in de sector waar de schermen op staan; daarvoor zonder
  // sector, zoals altijd (alle zichtbare = ICT).
  const meta = useMeta();
  const actief = useActieveSector();
  const zoekSector = toonPropositie(meta) && actief ? [actief] : undefined;
  // Een pilotpropositie zoekt regelingen in die van haar school, ICT zonder filter.
  const regelSector = (regelSectoren ?? []).filter((s2) => s2 !== "ict");

  useEffect(() => {
    inputRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setGroepen({});
      return;
    }
    // Eén soort gekozen: alleen die vraag, en dan een langere lijst.
    const gevraagd = soort === "alles" ? GROEPEN : [soort];
    const aantal = soort === "alles" ? 5 : 15;
    let alive = true;
    setGroepen(Object.fromEntries(gevraagd.map((g) => [g, "bezig"])));
    const zet = (g: ZoekSoort, stand: GroepStand) => {
      if (alive) setGroepen((oud) => ({ ...oud, [g]: stand }));
    };
    // Elke groep komt los binnen; een trage of mislukte groep houdt de rest niet op.
    const vraag = (g: ZoekSoort, haal: () => Promise<Hit[]>) => {
      if (!gevraagd.includes(g)) return;
      Promise.resolve()
        .then(haal)
        .then(
          (treffers) => zet(g, treffers),
          () => zet(g, "fout"),
        );
    };
    const timer = window.setTimeout(() => {
      vraag("trend", async () => {
        if (laagAan) {
          return zoekLaagTrends(await trendsVoorKaart(), q, aantal).map(({ trend: t, meta }) => ({
            kind: "laagtrend" as const,
            id: t.id,
            title: t.titel,
            meta,
          }));
        }
        const trends = await api.trends({ search: q, pageSize: aantal, laag: "drager", sector: zoekSector });
        return trends.groups.map(({ trend: t }) => ({
          kind: "trend" as const,
          id: t.id,
          title: t.displayTitle || t.title,
          meta: `${t.category ?? "zonder categorie"} · ${t.sourceCount} bronnen`,
          score: t.uitlichtscore ?? undefined,
          trend: t,
        }));
      });
      vraag("skill", async () => {
        const skills = await api.skills({ search: q, pageSize: aantal, sector: zoekSector });
        // De skill-lijst is sinds W1c sessie 2 gegroepeerd, net als trends:
        // de zoekresultaten zijn de groepen plus wat eronder hangt.
        return (skills.groups ?? [])
          .flatMap((g) => [g.skill, ...g.children])
          .map((s) => ({
            kind: "skill" as const,
            id: s.id,
            title: s.displayTitle || s.name,
            meta: `${s.category ?? "zonder categorie"} · ${s.trendCount} ${laagAan ? "bevindingen" : "trends"}`,
            score: s.scores?.priority,
            skill: s,
          }));
      });
      vraag("bron", async () => {
        const sources = await api.sources({ search: q, pageSize: aantal, sector: zoekSector });
        return sources.items.map((s) => ({
          kind: "bron" as const,
          id: s.id,
          title: s.title,
          // Methode v3: wie het zegt in plaats van het gezagscijfer.
          meta:
            s.wie !== undefined
              ? [s.publisher, s.wie ? (WIE_WOORD[s.wie] ?? s.wie) : null].filter(Boolean).join(" · ")
              : `${s.publisher} · gezag ${s.authority}`,
          source: s,
        }));
      });
      vraag("regeling", async () => {
        const lijst = await haalFundingLijst({ zoek: q, pageSize: aantal, sector: regelSector.length ? regelSector : undefined });
        return lijst.items.map((f) => ({
          kind: "regeling" as const,
          id: f.id,
          title: f.naam,
          meta: [f.naam !== f.verstrekker ? f.verstrekker : null, STATUS_WOORD[f.status] ?? f.status].filter(Boolean).join(" · "),
          funding: f,
        }));
      });
    }, 220); // even wachten met typen, anders vier aanroepen per toetsaanslag
    return () => {
      alive = false;
      window.clearTimeout(timer);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps -- zoekSector en regelSector zijn afgeleide lijsten
  }, [query, soort, laagAan, zoekSector?.[0], regelSector.join("|")]);

  const zichtbaar = (soort === "alles" ? GROEPEN : [soort]).filter((g) => groepen[g] !== undefined);
  const nogBezig = zichtbaar.some((g) => groepen[g] === "bezig");
  const alleFout = zichtbaar.length > 0 && zichtbaar.every((g) => groepen[g] === "fout");
  const niets = !nogBezig && !alleFout && zichtbaar.every((g) => { const v = groepen[g]; return v === "fout" || (Array.isArray(v) && v.length === 0); });
  const zoekt = query.trim().length > 1;

  const pick = (hit: Hit) => {
    if (hit.kind === "laagtrend") {
      setLaagOpen({ id: hit.id, titel: hit.title });
      return;
    }
    if (hit.kind === "trend") onSelectTrend?.(hit.trend);
    if (hit.kind === "skill") onSelectSkill?.(hit.skill);
    if (hit.kind === "bron") onSelectSource?.(hit.source);
    if (hit.kind === "regeling") onSelectFunding?.(hit.funding);
    onClose();
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 300,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        paddingTop: 100,
        backdropFilter: "blur(4px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(660px,90vw)",
          background: color.surface,
          borderRadius: 16,
          boxShadow: "0 24px 60px rgba(0,0,0,0.2)",
          overflow: "hidden",
          maxHeight: "70vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            padding: "20px 22px",
            flexShrink: 0,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
            <circle cx="8" cy="8" r="5.5" stroke={color.faint} strokeWidth="1.5" />
            <path d="M13 13l2.5 2.5" stroke={color.faint} strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={PLAATSHOUDER[soort]}
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              fontSize: 16,
              color: color.ink,
              fontFamily: "inherit",
              background: "transparent",
            }}
          />
          <button
            onClick={onClose}
            style={{
              background: color.lineFaint,
              border: "none",
              color: color.soft,
              padding: "5px 12px",
              borderRadius: 6,
              cursor: "pointer",
              fontSize: size.meta,
              fontFamily: "inherit",
              fontWeight: weight.regular,
            }}
          >
            esc
          </button>
        </div>
        <div
          role="group"
          aria-label="Zoek in"
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
            padding: "0 22px 14px",
            borderBottom: `1px solid ${color.lineFaint}`,
            flexShrink: 0,
          }}
        >
          {(["alles", ...GROEPEN] as const).map((k) => {
            const aan = soort === k;
            return (
              <button
                key={k}
                type="button"
                aria-pressed={aan}
                onClick={() => {
                  setSoort(k);
                  setLaagOpen(null);
                  inputRef.current?.focus();
                }}
                style={{
                  background: aan ? color.ink : color.lineFaint,
                  color: aan ? color.surface : color.soft,
                  border: "none",
                  padding: "5px 12px",
                  borderRadius: radius.pill,
                  fontSize: size.meta,
                  fontWeight: aan ? weight.medium : weight.regular,
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                {k === "alles" ? "Alles" : GROEP_TITEL[k]}
              </button>
            );
          })}
        </div>

        {laagOpen && (
          <div style={{ overflowY: "auto" }}>
            <button
              type="button"
              onClick={() => setLaagOpen(null)}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                background: color.surface,
                border: "none",
                borderBottom: `1px solid ${color.lineFaint}`,
                padding: "9px 22px",
                fontFamily: "inherit",
                fontSize: 12,
                fontWeight: weight.medium,
                color: color.muted,
                cursor: "pointer",
              }}
            >
              ← Terug naar de resultaten
            </button>
            <LaagTrend trendId={laagOpen.id} titel={laagOpen.titel} />
          </div>
        )}
        <div style={{ overflowY: "auto", display: laagOpen ? "none" : undefined }}>
          {zoekt && alleFout && (
            <div
              style={{
                padding: "28px 22px",
                textAlign: "center",
                color: color.accentRed,
                fontSize: size.body,
              }}
            >
              Zoeken lukte niet — geen verbinding met de leeslaag.
            </div>
          )}
          {zoekt &&
            !alleFout &&
            zichtbaar.map((g) => {
              const stand = groepen[g];
              // Bij één gekozen soort is het kopje dubbel: de knop zegt het al.
              const kop = soort === "alles";
              if (Array.isArray(stand) && stand.length === 0 && kop) return null;
              return (
                <section key={g} aria-label={GROEP_TITEL[g]} aria-busy={stand === "bezig"}>
                  {kop && (
                    <div
                      style={{
                        padding: "12px 22px 4px",
                        fontSize: size.meta,
                        fontWeight: weight.medium,
                        color: color.faint,
                      }}
                    >
                      {GROEP_TITEL[g]}
                    </div>
                  )}
                  {stand === "bezig" && (
                    <div style={{ padding: kop ? "6px 22px 14px" : "36px 22px", textAlign: kop ? "left" : "center", color: color.faint, fontSize: size.meta }}>
                      {GROEP_TITEL[g]} zoekt nog…
                    </div>
                  )}
                  {stand === "fout" && (
                    <div style={{ padding: "6px 22px 14px", color: color.accentRed, fontSize: size.meta }}>
                      {GROEP_TITEL[g]} lukte niet. De andere soorten staan er wel.
                    </div>
                  )}
                  {Array.isArray(stand) &&
                    stand.map((hit) => (
                      <button
                        key={`${hit.kind}-${hit.id}`}
                        onClick={() => pick(hit)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 12,
                          width: "100%",
                          padding: "14px 22px",
                          background: color.surface,
                          border: "none",
                          borderBottom: `1px solid ${color.surfaceQuote}`,
                          cursor: "pointer",
                          fontFamily: "inherit",
                          textAlign: "left",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = color.surfaceQuote;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = color.surface;
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                              marginBottom: 3,
                            }}
                          >
                            <span
                              style={{
                                fontSize: 9,
                                fontWeight: weight.bold,
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                                color: color.soft,
                                background: color.lineFaint,
                                borderRadius: 3,
                                padding: "2px 5px",
                                flexShrink: 0,
                              }}
                            >
                              {KIND_LABEL[hit.kind]}
                            </span>
                            <span
                              style={{
                                fontSize: size.lead,
                                fontWeight: weight.medium,
                                color: color.ink,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {hit.title}
                            </span>
                          </div>
                          <div style={{ fontSize: size.meta, color: color.faint }}>{hit.meta}</div>
                        </div>
                        {typeof hit.score === "number" && (
                          <span
                            style={{ fontSize: 20, fontWeight: weight.bold, color: color.ink, flexShrink: 0 }}
                          >
                            {hit.score.toFixed(1)}
                          </span>
                        )}
                      </button>
                    ))}
                </section>
              );
            })}
          {zoekt && niets && (
            <div
              style={{
                padding: "36px 22px",
                textAlign: "center",
                color: color.faint,
                fontSize: size.body,
              }}
            >
              Niets gevonden voor “{query}”{soort === "alles" ? "" : ` in de ${GROEP_TITEL[soort].toLowerCase()}`}.
            </div>
          )}
          {query.length === 0 && (
            <div style={{ padding: "18px 22px" }}>
              <div
                style={{
                  fontSize: 12,
                  fontWeight: weight.medium,
                  color: color.faint,
                  letterSpacing: "0.07em",
                  textTransform: "uppercase",
                  marginBottom: 10,
                }}
              >
                Voorbeelden
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {/* Termen die in dit corpus echt bestaan — geen verzonnen tags —
                    en die bij de propositie horen waar je op staat. */}
                {zoekVoorbeelden(actief).map(
                  (tag) => (
                    <button
                      key={tag}
                      onClick={() => setQuery(tag)}
                      style={{
                        background: color.lineFaint,
                        border: "none",
                        color: color.soft,
                        padding: "7px 14px",
                        borderRadius: radius.pill,
                        fontSize: size.meta,
                        fontWeight: weight.regular,
                        cursor: "pointer",
                        fontFamily: "inherit",
                      }}
                    >
                      {tag}
                    </button>
                  ),
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
